import { useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback, useRef } from 'react';
import type { z } from 'zod';
import type { OriginActionValueType } from '@/lib/analytics/Avo';
import { TabValue, type TabValueType } from '@/lib/types/itemPanel';
import type { itemPanelSearchSchema } from '@/lib/zod-schemas/itemPanelSearchSchema';

export type ItemPanelSearchParams = z.output<typeof itemPanelSearchSchema>;

export interface OpenItemPanelParams extends Partial<ItemPanelSearchParams> {
  originAction?: OriginActionValueType;
  onItemOpened?: (panelTab: TabValueType) => void;
}

export const useItemPanelNavigation = () => {
  const navigate = useNavigate();
  const currentSearch = useSearch({ strict: false }) as ItemPanelSearchParams;
  const currentSearchRef = useRef(currentSearch);
  currentSearchRef.current = currentSearch;

  const openItemPanel = useCallback(
    (params: OpenItemPanelParams) => {
      const { originAction, onItemOpened, ...panelParams } = params;

      const defaultParams: Partial<ItemPanelSearchParams> = {
        expanded: true,
        panelTab: TabValue.PLAN_SCHEDULING,
        ...panelParams,
      };

      const newSearch = {
        ...currentSearchRef.current,
        ...defaultParams,
      } as ItemPanelSearchParams;

      if (panelParams.itemId) {
        onItemOpened?.(defaultParams.panelTab ?? TabValue.PLAN_SCHEDULING);
      }

      navigate({ to: '.', search: newSearch, resetScroll: false });
    },
    [navigate],
  );

  const closeItemPanel = useCallback(() => {
    const { itemId, panelTab, expanded, ...restSearch } = currentSearchRef.current;

    navigate({ to: '.', search: restSearch, resetScroll: false });
  }, [navigate]);

  return { openItemPanel, closeItemPanel };
};
